/* eslint-disable react/prop-types */
import { FC, useState } from "react";
import { ArrowBendUpLeft, Trash } from "@phosphor-icons/react";
import { useUser } from "src/hooks";
import dayjs from "dayjs";
import CommentInput from "src/features/SocialFeedContent/components/CommentInput/CommentInput";
import {
	useCreateComment,
	useDeleteComment
} from "src/features/SocialFeedContent/hooks";

interface CommentItemProps {
	commentId: number;
	userId: string;
	userName?: string | null;
	body: string;
	date: string;
	reactionsCount: number;
	isReply?: boolean;
	onReply?: () => void;
	onDelete?: () => void;
	isDeleting?: boolean;
}

const CommentItem: FC<CommentItemProps> = ({
	userId,
	userName,
	body,
	date,
	reactionsCount,
	isReply = false,
	onReply,
	onDelete,
	isDeleting = false
}) => {
	const needsFetch = !userName && Boolean(userId);
	const { data: user } = useUser(userId, false, {
		enabled: needsFetch
	});

	const displayName = userName || user?.name || null;

	const initials = displayName
		? displayName
				.split(" ")
				.slice(0, 2)
				.map((n: string) => n[0])
				.join("")
				.toUpperCase()
		: "";

	return (
		<div className={isReply ? "ml-8 border-l-2 border-zinc-200 pl-3" : ""}>
			<div className="flex gap-2.5 py-2">
				<div className="w-6 h-6 shrink-0 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-[10px] font-medium">
					{initials}
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="text-[13px] font-medium text-foreground">
							{displayName ?? "Unknown user"}
						</span>
						<span className="text-[11px] text-muted-foreground">
							{dayjs(date).format("DD MMM YYYY, HH:mm")}
						</span>
					</div>
					<p className="text-[13px] text-foreground mt-0.5">{body}</p>
					<div className="flex items-center gap-3 mt-1">
						{reactionsCount > 0 && (
							<span className="text-[11px] text-muted-foreground">
								{reactionsCount}{" "}
								{reactionsCount === 1 ? "reaction" : "reactions"}
							</span>
						)}
						{onReply && (
							<button
								type="button"
								onClick={onReply}
								className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
							>
								<ArrowBendUpLeft size={12} weight="duotone" />
								Reply
							</button>
						)}
						{onDelete && (
							<button
								type="button"
								onClick={onDelete}
								disabled={isDeleting}
								className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-red-500 transition-colors disabled:opacity-50"
							>
								{isDeleting ? (
									<div className="w-3 h-3 border border-red-500 border-t-transparent rounded-full animate-spin" />
								) : (
									<Trash size={12} weight="duotone" />
								)}
								Delete
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

interface CommentThreadProps {
	postId: string;
	comments: Comment[];
	hasMore: boolean;
	isLoadingMore: boolean;
	onLoadMore: () => void;
}

const CommentThread: FC<CommentThreadProps> = ({
	postId,
	comments,
	hasMore,
	isLoadingMore,
	onLoadMore
}) => {
	const [replyTarget, setReplyTarget] = useState<{
		commentId: number;
		userName: string;
	} | null>(null);

	const createComment = useCreateComment();
	const deleteComment = useDeleteComment();

	const handleSubmitComment = (
		body: string,
		parentCommentId?: number | null
	) => {
		createComment.mutate(
			{
				social_post: postId,
				body,
				parent_comment: parentCommentId ?? null
			},
			{
				onSuccess: () => {
					setReplyTarget(null);
				}
			}
		);
	};

	const handleDelete = (commentId: number) => {
		deleteComment.mutate(commentId);
	};

	return (
		<div className="border-t border-border">
			<div className="px-4 pb-1">
				{comments.length === 0 && (
					<div className="px-4 py-6 text-center">
						<p className="text-sm text-muted-foreground">No comments yet</p>
					</div>
				)}
				<div className="divide-y divide-border/50">
					{comments.map((comment) => (
						<div key={comment.id}>
							<CommentItem
								commentId={comment.id}
								userId={comment.user}
								userName={comment.userName}
								body={comment.body}
								date={comment.commentDate}
								reactionsCount={comment.reactions.length}
								onReply={() =>
									setReplyTarget({
										commentId: comment.id,
										userName: comment.userName ?? "Unknown user"
									})
								}
								onDelete={() => handleDelete(comment.id)}
								isDeleting={
									deleteComment.isLoading &&
									deleteComment.variables === comment.id
								}
							/>
							{comment.commentReplies.map((reply) => (
								<CommentItem
									key={reply.id}
									commentId={reply.id}
									userId={reply.user}
									userName={reply.userName}
									body={reply.body}
									date={reply.commentDate}
									reactionsCount={reply.reactions.length}
									isReply
									onReply={() =>
										setReplyTarget({
											commentId: comment.id,
											userName: reply.userName ?? "Unknown user"
										})
									}
									onDelete={() => handleDelete(reply.id)}
									isDeleting={
										deleteComment.isLoading &&
										deleteComment.variables === reply.id
									}
								/>
							))}
						</div>
					))}
				</div>

				{hasMore && (
					<button
						type="button"
						onClick={onLoadMore}
						disabled={isLoadingMore}
						className="w-full text-center py-2 text-[13px] text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
					>
						{isLoadingMore ? (
							<span className="flex items-center justify-center gap-2">
								<span className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
								Loading...
							</span>
						) : (
							"Show more comments"
						)}
					</button>
				)}
			</div>

			<CommentInput
				parentCommentId={replyTarget?.commentId ?? null}
				replyingTo={replyTarget?.userName ?? null}
				onCancelReply={() => setReplyTarget(null)}
				onSubmit={handleSubmitComment}
				isSubmitting={createComment.isLoading}
			/>
		</div>
	);
};

export default CommentThread;
